import { MessageSquare, Smartphone, Mic, Phone, Layers } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/hooks/useLanguage";

const channelMeta = [
  { icon: MessageSquare, color: "bg-chathero-mint/20", iconColor: "text-chathero-mint" },
  { icon: Smartphone, color: "bg-secondary", iconColor: "text-primary" },
  { icon: Mic, color: "bg-chathero-orange/20", iconColor: "text-chathero-orange" },
  { icon: Phone, color: "bg-primary/10", iconColor: "text-primary" },
] as const;

const KanaeleSection = () => {
  const { ref, isVisible } = useScrollAnimation();
  const { t, tObject } = useLanguage();
  const channels = tObject<Array<{ name: string; description: string }>>("aiAgent.kanaele.channels");

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      className={cn(
        "py-16 md:py-24 bg-background transition-all duration-700 ease-out",
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      )}
    >
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center">
            <Badge variant="outline" className="mb-4 text-sm font-semibold text-primary uppercase tracking-wider">
              {t("aiAgent.kanaele.badge")}
            </Badge>

            <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-6">
              {t("aiAgent.kanaele.title")}
            </h2>
          </div>

          <p className="text-lg text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
            {t("aiAgent.kanaele.intro")}
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-8">
            {channels.map((channel, index) => {
              const meta = channelMeta[index];
              return (
                <div
                  key={channel.name}
                  className={cn(
                    "bg-secondary/50 rounded-2xl p-5 md:p-6 border border-border hover:shadow-md hover:border-chathero-mint/50 transition-all text-center",
                    isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                  )}
                  style={{
                    transitionDelay: isVisible ? `${index * 100}ms` : "0ms",
                    transitionDuration: "500ms",
                  }}
                >
                  <div className={cn("w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center mx-auto mb-3", meta.color)}>
                    <meta.icon className={cn("w-6 h-6 md:w-7 md:h-7", meta.iconColor)} />
                  </div>
                  <h3 className="text-base md:text-lg font-semibold text-primary mb-1">{channel.name}</h3>
                  <p className="text-xs md:text-sm text-muted-foreground">{channel.description}</p>
                </div>
              );
            })}
          </div>

          <div
            className={cn(
              "bg-primary rounded-2xl p-6 md:p-8 text-center transition-all duration-500",
              isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"
            )}
            style={{ transitionDelay: isVisible ? "500ms" : "0ms" }}
          >
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-chathero-mint/20 flex items-center justify-center">
                <Layers className="w-6 h-6 text-chathero-mint" />
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-primary-foreground">
                {t("aiAgent.kanaele.omnichannelTitle")}
              </h3>
            </div>
            <p className="text-primary-foreground/80 max-w-xl mx-auto">
              {t("aiAgent.kanaele.omnichannelDesc")}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default KanaeleSection;
